import React, { useEffect, useMemo, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import { setClickedSymbolData } from "../../../rtk/slices/tradingSlice/tradingSlice";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, } from "reactstrap";
import { fetchMarketDataHistory } from "../../../rtk/slices/marketDataHistorySlice/marketDataHistorySlice";
import IndicatorsSettings from "./IndicatorsSettings";
import { useLocation } from "react-router-dom";

// indicator funtions start 
function calculateRSI(data, period = 14) {
  let gains = 0, losses = 0;
  const rsi = [];

  for (let i = 1; i < data.length; i++) {
    const change = data[i].close - data[i - 1].close;
    if (change >= 0) gains += change;
    else losses -= change;

    if (i >= period) {
      const avgGain = gains / period;
      const avgLoss = losses / period;
      const rs = avgGain / (avgLoss || 1);
      const rsiValue = 100 - 100 / (1 + rs);
      rsi.push({ time: data[i].time, value: parseFloat(rsiValue.toFixed(2)) });

      const oldChange = data[i - period + 1].close - data[i - period].close;
      if (oldChange >= 0) gains -= oldChange;
      else losses += oldChange;
    }
  }

  return rsi;
}

function calculateMACD(data, fastPeriod = 12, slowPeriod = 26) {
  const fast = calculateEMA(data, fastPeriod);
  const slow = calculateEMA(data, slowPeriod);
  const macd = [];
  for (let i = 0; i < fast.length && i < slow.length; i++) {
    macd.push({ time: fast[i].time, value: parseFloat((fast[i].value - slow[i].value).toFixed(4)) });
  }
  return macd;
}

function calculateBB(data, period = 20) {
  const upper = [], lower = [];
  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1);
    const mean = slice.reduce((sum, d) => sum + d.close, 0) / period;
    const variance = slice.reduce((sum, d) => sum + Math.pow(d.close - mean, 2), 0) / period;
    const stdDev = Math.sqrt(variance);
    upper.push({ time: data[i].time, value: parseFloat((mean + 2 * stdDev).toFixed(4)) });
    lower.push({ time: data[i].time, value: parseFloat((mean - 2 * stdDev).toFixed(4)) });
  }
  return { upper, lower };
}


function calculateEMA(data, period = 14) {
  const ema = [];
  let k = 2 / (period + 1);
  let emaPrev;

  data.forEach((point, index) => {
    const price = point.close;
    if (index === 0) {
      emaPrev = price;
    } else {
      emaPrev = price * k + emaPrev * (1 - k);
    }

    ema.push({
      time: point.time,
      value: parseFloat(emaPrev.toFixed(4)),
    });
  });

  return ema;
}

function calculateATR(data, period = 14) {
  const atr = [];
  for (let i = 1; i < data.length; i++) {
    const currentHigh = data[i].high;
    const currentLow = data[i].low;
    const previousClose = data[i - 1].close;
    const trueRange = Math.max(
      currentHigh - currentLow,
      Math.abs(currentHigh - previousClose),
      Math.abs(currentLow - previousClose)
    );
    atr.push(trueRange);
  }

  const atrResult = [];
  for (let i = period - 1; i < atr.length; i++) {
    const sum = atr.slice(i - period + 1, i + 1).reduce((acc, val) => acc + val, 0);
    atrResult.push({ time: data[i + 1].time, value: sum / period });
  }
  return atrResult;
}
function calculateStochastic(data, period = 14) {
  const kLine = [];
  const dLine = [];

  for (let i = period - 1; i < data.length; i++) {
    const highSlice = data.slice(i - period + 1, i + 1).map(d => d.high);
    const lowSlice = data.slice(i - period + 1, i + 1).map(d => d.low);
    const highestHigh = Math.max(...highSlice);
    const lowestLow = Math.min(...lowSlice);
    const currentClose = data[i].close;
    const percentK = ((currentClose - lowestLow) / (highestHigh - lowestLow)) * 100;
    kLine.push({ time: data[i].time, value: percentK });

    if (kLine.length >= 3) {
      const last3K = kLine.slice(-3).map(p => p.value);
      const avgD = last3K.reduce((acc, val) => acc + val, 0) / 3;
      dLine.push({ time: data[i].time, value: avgD });
    }
  }

  return { kLine, dLine };
}
function calculateWMA(data, period = 14) {
  const wma = [];
  const denominator = (period * (period + 1)) / 2;
  for (let i = period - 1; i < data.length; i++) {
    let weightedSum = 0;
    for (let j = 0; j < period; j++) {
      weightedSum += data[i - j].close * (period - j);
    }
    wma.push({ time: data[i].time, value: weightedSum / denominator });
  }
  return wma;
}
function calculateVolume(data) { }
function calculateSMA(data, period = 14) {
  const sma = [];
  for (let i = period - 1; i < data.length; i++) {
    const sum = data.slice(i - period + 1, i + 1).reduce((acc, val) => acc + val.close, 0);
    sma.push({ time: data[i].time, value: sum / period });
  }
  return sma;
}

// indicator functions end 

// Function to get icon and color based on symbol
const getSymbolDetails = (symbol) => {
  const symbolLower = symbol.toLowerCase();

  // Cryptocurrencies
  if (symbolLower.includes('btc')) {
    return { icon: 'ri-bit-coin-fill', color: '#F7931A' };
  } else if (symbolLower.includes('eth')) {
    return { icon: 'ri-eth-fill', color: '#627EEA' };
  } else if (symbolLower.includes('xrp')) {
    return { icon: 'ri-xrp-fill', color: '#23292F' };
  }

  // Precious Metals
  else if (symbolLower.includes('xau') || symbolLower.includes('gold')) {
    return { icon: 'ri-copper-coin-fill', color: '#FFD700' };
  }

  // Stock Indices
  else if (symbolLower.includes('ustec')) {
    return { icon: 'ri-line-chart-fill', color: '#2962FF' };
  }

  // Currency Pairs
  else if (symbolLower.startsWith('usd')) {
    return { icon: 'ri-money-dollar-circle-fill', color: '#85bb65' };
  } else if (symbolLower.startsWith('eur')) {
    return { icon: 'ri-money-euro-circle-fill', color: '#0052B4' };
  } else if (symbolLower.startsWith('gbp') || symbolLower.endsWith('gbp')) {
    return { icon: 'ri-money-pound-circle-fill', color: '#C8102E' };
  } else if (symbolLower.startsWith('jpy') || symbolLower.endsWith('jpy')) {
    return { icon: 'ri-money-cny-circle-fill', color: '#BC002D' };
  } else if (symbolLower.startsWith('aud') || symbolLower.endsWith('aud')) {
    return { icon: 'ri-money-dollar-circle-fill', color: '#00843D' };
  } else if (symbolLower.startsWith('cad') || symbolLower.endsWith('cad')) {
    return { icon: 'ri-money-dollar-circle-fill', color: '#FF0000' };
  } else if (symbolLower.startsWith('nzd') || symbolLower.endsWith('nzd')) {
    return { icon: 'ri-money-dollar-circle-fill', color: '#00247D' };
  } else if (symbolLower.startsWith('chf') || symbolLower.endsWith('chf')) {
    return { icon: 'ri-money-franc-circle-fill', color: '#FF0000' };
  } else if (symbolLower.startsWith('zar') || symbolLower.endsWith('zar')) {
    return { icon: 'ri-money-dollar-circle-fill', color: '#007A4D' };
  }

  return { icon: 'ri-exchange-fill', color: '#6c757d' };
};

const timeframes = {
  "1m": 60,
  "5m": 300,
  "15m": 900,
  "1h": 3600,
  "1D": 86400,
};

const symbolFullNames = {
  BTCUSD: "Bitcoin / US Dollar",
  XRPUSD: "Ripple / US Dollar",
  EURUSD: "Euro / US Dollar",
  GBPUSD: "British Pound / US Dollar",
  USDJPY: "US Dollar / Japanese Yen",
  AUDUSD: "Australian Dollar / US Dollar",
  USDCAD: "US Dollar / Canadian Dollar",
  AUDJPY: "Australian Dollar / Japanese Yen",
  EURCHF: "Euro / Swiss Franc",
  EURGBP: "Euro / British Pound",
  EURJPY: "Euro / Japanese Yen",
  NZDUSD: "New Zealand Dollar / US Dollar",
  AUDCAD: "Australian Dollar / Canadian Dollar",
  CADCHF: "Canadian Dollar / Swiss Franc",
  CADJPY: "Canadian Dollar / Japanese Yen",
  CHFJPY: "Swiss Franc / Japanese Yen",
  EURAUD: "Euro / Australian Dollar",
  GBPJPY: "British Pound / Japanese Yen",
  NZDCAD: "New Zealand Dollar / Canadian Dollar",
  NZDJPY: "New Zealand Dollar / Japanese Yen",
  USDCHF: "US Dollar / Swiss Franc",
  EURNZD: "Euro / New Zealand Dollar",
  GBPAUD: "British Pound / Australian Dollar",
  GBPCAD: "British Pound / Canadian Dollar",
  GBPNZD: "British Pound / New Zealand Dollar",
  XAGUSD: "Silver / US Dollar",
  XAUUSD: "Gold / US Dollar",
  USDCNH: "US Dollar / Chinese Yuan",
  USDZAR: "US Dollar / South African Rand",
  BTCUSD_ex1: "Bitcoin / US Dollar",
  ETHUSD_ex1: "Ethereum / US Dollar",
  AUDNZD: "Australian Dollar / New Zealand Dollar",
  USTEC: "US Tech 100 Index",
  XRPUSD_ex1: "Ripple / US Dollar",
  USTEC_ex1: "US Tech 100 Index",
  USOil_ex1: "Crude Oil (WTI)",
  AUDCHF: "Australian Dollar / Swiss Franc",
  EURCAD: "Euro / Canadian Dollar",
  GOLDFUT_APR25: "Gold Futures (April 2025)",
  XTI: "Crude Oil (West Texas Intermediate)",
  GBPCHF: "British Pound / Swiss Franc",
  NZDCHF: "New Zealand Dollar / Swiss Franc",
};

const seriesTypes = ['Candlestick', 'Area', 'Bar', 'Baseline', 'Histogram', 'Line'];

const TradingViewChart = () => {
  const location = useLocation();
  const chartContainerRef = useRef(null);
  const tooltipRef = useRef(null);
  const dispatch = useDispatch();
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const lastCandleRef = useRef(null);
  const lastFetchRef = useRef(0);
  const visibleRangeRef = useRef(null);
  const markersRef = useRef([]);

  const orderStatus = useSelector((state) => state.order.orderStatus);
  const [modal, setModal] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState("1m");
  const [selectedSeriesType, setSelectedSeriesType] = useState("Candlestick");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [seriesDropdownOpen, setSeriesDropdownOpen] = useState(false);
  const [bars, setbars] = useState(100);
  const [isLoading, setIsLoading] = useState(false);
  const [ohlcData, setOhlcData] = useState({ open: 0, high: 0, low: 0, close: 0, time: null, color: "#2DA479" });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipData, setTooltipData] = useState(null);

  // --- Indicator State and Refs ---
  const [indicatorDropdownOpen, setIndicatorDropdownOpen] = useState(false);
  const [activeIndicators, setActiveIndicators] = useState([]);
  const [emaPeriod, setEmaPeriod] = useState(14);
  const [smaPeriod, setSmaPeriod] = useState(14);
  const [wmaPeriod, setWmaPeriod] = useState(14);
  const [emaLineColor, setEmaLineColor] = useState();
  const [smaLineColor, setSmaLineColor] = useState();
  const [wmaLineColor, setWmaLineColor] = useState();
  const [bbPeriod, setBbPeriod] = useState(20);

  const emaSeriesRef = useRef(null);
  const smaSeriesRef = useRef(null);
  const wmaSeriesRef = useRef(null);

  const bbUpperSeriesRef = useRef(null);
  const bbLowerSeriesRef = useRef(null);



  const selectedSymbol = useSelector((state) => state.trading.clickedSymbolData) || "BTCUSD.ex1";
  const symbols = useSelector((state) => state.trading.symbols);
  const layoutMode = useSelector((state) => state.Layout.layoutModeType);
  const { data, status } = useSelector((state) => state.marketDataHistory);
  const selectedSymbolBid = useSelector((state) => state.trading.clickedSymbolBid);

  // console.log('order status', orderStatus)

  const toggleModal = (val) => {
    setModal(!modal)
    setSelectedIndicator(val);
  };

  //  function handleSettings(val) {
  //   console.log(val)
  //   switch (val.indicatorName) {
  //     case 'EMA':
  //       setEmaPeriod(localStorage.setItem("emaPeriod", val.periodVal));
  //       setEmaLineColor(localStorage.setItem("emaLineColor", val.lineColor));
  //     case 'SMA':
  //       setSmaPeriod(localStorage.setItem("smaPeriod", val.periodVal));
  //       setSmaLineColor(localStorage.setItem("smaLineColor", val.lineColor));
  //     case 'WMA':
  //       setWmaPeriod(localStorage.setItem("wmaPeriod", val.periodVal));
  //       setWmaLineColor(localStorage.setItem("wmaLineColor", val.lineColor));
  //   }
  // };

  // --- Indicator Toggle Helper ---

  function handleSettings(val) {
    switch (val.indicatorName) {
      case 'EMA':
        localStorage.setItem("emaPeriod", val.periodVal);
        localStorage.setItem("emaLineColor", val.lineColor);
        setEmaPeriod(val.periodVal);
        setEmaLineColor(val.lineColor);
        break;
      case 'SMA':
        localStorage.setItem("smaPeriod", val.periodVal);
        localStorage.setItem("smaLineColor", val.lineColor);
        setSmaPeriod(val.periodVal);
        setSmaLineColor(val.lineColor);
        break;
      case 'WMA':
        localStorage.setItem("wmaPeriod", val.periodVal);
        localStorage.setItem("wmaLineColor", val.lineColor);
        setWmaPeriod(val.periodVal);
        setWmaLineColor(val.lineColor);
        break;
      default:
        break;
    }
  }

  const toggleIndicator = (indicator) => {
    if (activeIndicators.includes(indicator)) {
      setActiveIndicators(prev => prev.filter(ind => ind !== indicator));
    } else {
      setActiveIndicators(prev => [...prev, indicator]);
    }
  };

  // --- Clear All Indicators ---
  const clearAllIndicators = () => {
    setActiveIndicators([]);
  };

  const mappedData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    const seenTimes = new Set();
    const processedData = data
      .map(item => {

        const formattedTime = Math.floor(new Date(item.time).getTime() / 1000); // Convert to Unix timestamp

        return {
          time: formattedTime,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
        }
      })
      .filter(item => {
        if (seenTimes.has(item.time)) return false;
        seenTimes.add(item.time);
        return true;
      })
      .sort((a, b) => a.time - b.time);

    // return processedData?.length > 0 ? processedData : [];
    return processedData.length > 0 ? processedData.slice(0, -1) : [];

  }, [data]);

  // console.log('mapped data',mappedData);


  const options = useMemo(() => (
    symbols ? Object.keys(symbols).map(symbolKey => {
      const { icon, color } = getSymbolDetails(symbolKey);
      return {
        value: symbolKey,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0' }}>
            <i
              className={icon}
              style={{
                fontSize: '25px',
                color: color,
                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            ></i>
            <span>{symbolKey}</span>
          </div>
        )
      };
    }) : []
  ), [symbols]);

  // function getSymbolFullName(symbol) {
  //   return symbolFullNames[symbol.replace(".ex1", "")] || symbol;
  // }

  function getSymbolFullName(symbol) {
    let cleanedSymbol = symbol;

    if (symbol.endsWith('.ex1')) {
      cleanedSymbol = symbol.replace('.ex1', '');
    } else if (symbol.endsWith('.')) {
      cleanedSymbol = symbol.slice(0, -1);
    }

    return symbolFullNames[cleanedSymbol] || cleanedSymbol;
  }
  const handleSelectChange = (selectedOption) => {
    if (!selectedOption) return;

    const fullName = getSymbolFullName(selectedOption.value);
    document.getElementById("chart-title").innerText = fullName;

    const newSymbol = selectedOption.value;
    setbars(100);
    visibleRangeRef.current = null;
    dispatch(setClickedSymbolData(newSymbol));

    if (seriesRef.current) {
      seriesRef.current.setData([]);
    }
    dispatch(fetchMarketDataHistory({ symbol: newSymbol, timeframe: 'M1', bars: 100 }));


  };

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  const toggleSeriesDropdown = () => setSeriesDropdownOpen(!seriesDropdownOpen);

  const handleScreenshot = () => {
    if (chartRef.current) {
      const canvas = chartRef.current.takeScreenshot();
      const link = document.createElement('a');
      link.download = `${selectedSymbol}-${selectedTimeframe}-${new Date().toISOString()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };
  // #DC3A48
  const createSeries = (chart, type) => {
    const isDarkMode = layoutMode === "dark";
    let decimalPlaces;
    if (!selectedSymbolBid || isNaN(selectedSymbolBid)) {
      decimalPlaces = 2
    }
    else {
      decimalPlaces = selectedSymbolBid?.toString().split('.')[1]?.length
    }

    const commonOptions = {
      upColor: "#2DA479",
      downColor: "#DC3A48",
      wickUpColor: "#2DA479",
      wickDownColor: "#DC3A48",
      priceFormat: {
        type: 'custom',
        minMove: Math.pow(10, -decimalPlaces),
        formatter: (price) => price.toFixed(decimalPlaces),
      },
    };

    switch (type) {
      case 'Candlestick':
        return chart.addCandlestickSeries({
          ...commonOptions,
          borderVisible: false,
        });
      case 'Area':
        return chart.addAreaSeries({
          lineColor: isDarkMode ? "#2962FF" : "#2962FF",
          topColor: isDarkMode ? "rgba(41, 98, 255, 0.3)" : "rgba(41, 98, 255, 0.3)",
          bottomColor: isDarkMode ? "rgba(41, 98, 255, 0)" : "rgba(41, 98, 255, 0)",
        });
      case 'Bar':
        return chart.addBarSeries(commonOptions);
      case 'Baseline':
        return chart.addBaselineSeries({
          baseValue: { type: 'price', price: 0 },
          topLineColor: "#2DA479",
          bottomLineColor: "#DC3A48",
        });
      case 'Histogram':
        return chart.addHistogramSeries({
          color: "#2DA479",
          base: 0,
          priceFormat: {
            type: 'volume',
          },
          priceLineVisible: false,
          lastValueVisible: false,
        });
      case 'Line':
        return chart.addLineSeries({
          color: isDarkMode ? "#2962FF" : "#2962FF",
          lineWidth: 2,
        });
      default:
        return chart.addCandlestickSeries(commonOptions);
    }
  };

  const transformData = (data, type) => {
    if (!Array.isArray(data) || data.length === 0) return [];

    return data.map(candle => {
      switch (type) {
        case 'Candlestick':
        case 'Bar':
          return {
            time: candle.time,
            open: candle.open,
            high: candle.high,
            low: candle.low,
            close: candle.close,
          };
        case 'Area':
        case 'Line':
        case 'Baseline':
          return {
            time: candle.time,
            value: candle.close,
          };
        case 'Histogram':
          const change = candle.close - candle.open;
          return {
            time: candle.time,
            value: Math.abs(change),
            color: change >= 0 ? "#2DA479" : "#DC3A48",
          };
        default:
          return candle;
      }
    });
  };

  const handleSeriesTypeChange = (type) => {
    setSelectedSeriesType(type);
    if (chartRef.current && seriesRef.current) {
      chartRef.current.removeSeries(seriesRef.current);
      seriesRef.current = createSeries(chartRef.current, type);
      if (selectedSymbol && mappedData.length > 0 && status === 'succeeded') {
        const transformedData = transformData(mappedData, type);
        seriesRef.current.setData(transformedData);


        // === Add EMA Indicator ===       
        // --- Remove all existing series before adding again ---
        if (emaSeriesRef.current) { chartRef.current.removeSeries(emaSeriesRef.current); emaSeriesRef.current = null; }
        if (smaSeriesRef.current) { chartRef.current.removeSeries(smaSeriesRef.current); smaSeriesRef.current = null; }
        if (wmaSeriesRef.current) { chartRef.current.removeSeries(wmaSeriesRef.current); wmaSeriesRef.current = null; }

        if (bbUpperSeriesRef.current) { chartRef.current.removeSeries(bbUpperSeriesRef.current); bbUpperSeriesRef.current = null; }
        if (bbLowerSeriesRef.current) { chartRef.current.removeSeries(bbLowerSeriesRef.current); bbLowerSeriesRef.current = null; }


        // --- Now Add Active Indicators '#FFA500'---
        activeIndicators.forEach(indicator => {
          if (indicator === 'EMA') {
            emaSeriesRef.current = chartRef.current.addLineSeries({ color: emaLineColor, lineWidth: 2 });
            const emaData = calculateEMA(mappedData, emaPeriod);
            emaSeriesRef.current.setData(emaData);
          }
          if (indicator === 'SMA') {
            smaSeriesRef.current = chartRef.current.addLineSeries({ color: smaLineColor, lineWidth: 2 });
            const smaData = calculateSMA(mappedData, smaPeriod);
            smaSeriesRef.current.setData(smaData);
          }
          if (indicator === 'WMA') {
            wmaSeriesRef.current = chartRef.current.addLineSeries({ color: wmaLineColor, lineWidth: 2 });
            const wmaData = calculateWMA(mappedData, wmaPeriod);
            wmaSeriesRef.current.setData(wmaData);
          }
          if (indicator === 'BollingerBands') {
            const { upper, lower } = calculateBB(mappedData, bbPeriod);
            bbUpperSeriesRef.current = chartRef.current.addLineSeries({ color: '#AAAAFF', lineWidth: 1 });
            bbLowerSeriesRef.current = chartRef.current.addLineSeries({ color: '#AAAAFF', lineWidth: 1 });
            bbUpperSeriesRef.current.setData(upper);
            bbLowerSeriesRef.current.setData(lower);
          }

        });




      }
      if (lastCandleRef.current) {
        const transformedCandle = transformData([lastCandleRef.current], type)[0];
        seriesRef.current.update(transformedCandle);
      }
    }
  };

  // const formatTooltipDate = (timestamp) => {
  //   const date = new Date(timestamp * 1000);
  //   return date.toLocaleString();
  // };
  const formatTooltipDate = (timestamp) => {
    return new Date(timestamp * 1000).toUTCString(); // Convert to UTC string
  };

  const updateTooltip = (param) => {
    if (!param.time || !param.point || !param.seriesData) {
      setTooltipVisible(false);
      return;
    }

    const coordinate = param.point;
    const data = param.seriesData.get(seriesRef.current);
    if (!data) {
      setTooltipVisible(false);
      return;
    }

    const tooltipContent = {
      time: formatTooltipDate(param.time),
      data: selectedSeriesType === 'Candlestick' || selectedSeriesType === 'Bar'
        ? {
          open: data.open,
          high: data.high,
          low: data.low,
          close: data.close,
        }
        : { value: data.value || data.close },
    };

    setTooltipData(tooltipContent);
    setTooltipPosition({
      x: coordinate.x,
      y: coordinate.y,
    });
    setTooltipVisible(true);
  };

  const addMarker = (time, position = 'aboveBar', color = 'blue', shape = 'arrowUp', text = 'Order Succeeded') => {
    const marker = {
      time, // in UNIX timestamp (seconds)
      position, // 'aboveBar' or 'belowBar'
      color,
      shape, // 'circle', 'square', 'arrowUp', 'arrowDown'
      text,
    };

    markersRef.current.push(marker); // store for future use
    if (seriesRef.current) {
      seriesRef.current.setMarkers(markersRef.current);
    }
  };

  const MIN_RANGE = 10;    // min number of bars (max zoom in)
  const MAX_RANGE = 300;   // max number of bars (max zoom out)

  const zoomIn = () => {
    const timeScale = chartRef.current.timeScale();
    const range = timeScale.getVisibleLogicalRange();
    if (!range) return;

    const size = range.to - range.from;
    if (size <= MIN_RANGE) return; // already at min zoom

    const center = (range.to + range.from) / 2;
    const newSize = Math.max(size * 0.7, MIN_RANGE); // clamp to MIN_RANGE

    timeScale.setVisibleLogicalRange({
      from: center - newSize / 2,
      to: center + newSize / 2,
    });
  };

  const zoomOut = () => {
    const timeScale = chartRef.current.timeScale();
    const range = timeScale.getVisibleLogicalRange();
    if (!range) return;

    const size = range.to - range.from;
    if (size >= MAX_RANGE) return; // already at max zoom

    const center = (range.to + range.from) / 2;
    const newSize = Math.min(size * 1.3, MAX_RANGE); // clamp to MAX_RANGE

    timeScale.setVisibleLogicalRange({
      from: center - newSize / 2,
      to: center + newSize / 2,
    });
  };

  const resetZoom = () => {
    const timeScale = chartRef.current.timeScale();
    timeScale.fitContent(); // This fits the whole series in view
  };


  // indicatore usefecct 
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("activeIndicators")) || [];
    setActiveIndicators(stored);
    setEmaPeriod(Number(localStorage.getItem("emaPeriod")) || 14);
    const emaLineColor = localStorage.getItem("emaLineColor");
    if (emaLineColor) {
      setEmaLineColor(emaLineColor);
    }

    setSmaPeriod(Number(localStorage.getItem("smaPeriod")) || 14);
    const smaLineColor = localStorage.getItem("smaLineColor")
    if (smaLineColor) {

      setSmaLineColor(smaLineColor);
    }

    setWmaPeriod(Number(localStorage.getItem("wmaPeriod")) || 14);
    const wmaLineColor = localStorage.getItem("wmaLineColor")
    if (wmaLineColor) {
      setWmaLineColor(wmaLineColor);
    }
    setBbPeriod(Number(localStorage.getItem("bbPeriod")) || 20);

  }, []);

  useEffect(() => {
    if (!chartRef.current || mappedData.length === 0) return;

    // Recalculate and update all active indicators
    activeIndicators.forEach((indicator) => {
      if (indicator === 'EMA' && emaSeriesRef.current) {
        const emaData = calculateEMA(mappedData, emaPeriod);
        emaSeriesRef.current.setData(emaData);
      }
      if (indicator === 'SMA' && smaSeriesRef.current) {
        const smaData = calculateSMA(mappedData, smaPeriod);
        smaSeriesRef.current.setData(smaData);
      }
      if (indicator === 'WMA' && wmaSeriesRef.current) {
        const wmaData = calculateWMA(mappedData, wmaPeriod);
        wmaSeriesRef.current.setData(wmaData);
      }
      if (indicator === 'BollingerBands') {
        const { upper, lower } = calculateBB(mappedData, bbPeriod);
        if (bbUpperSeriesRef.current) bbUpperSeriesRef.current.setData(upper);
        if (bbLowerSeriesRef.current) bbLowerSeriesRef.current.setData(lower);
      }

    });
  }, [lastCandleRef, mappedData, activeIndicators, emaPeriod, smaPeriod, wmaPeriod, bbPeriod]);

  useEffect(() => {
    localStorage.setItem("activeIndicators", JSON.stringify(activeIndicators));

  }, [
    activeIndicators,
    emaPeriod,
    emaLineColor,
    smaPeriod,
    smaLineColor,
    wmaPeriod,
    wmaLineColor,
    bbPeriod
  ]);

  useEffect(() => {
    if (orderStatus === 'succeeded' && lastCandleRef.current) {
      const { time } = lastCandleRef.current;
      addMarker(time, 'aboveBar', 'green', 'arrowUp', 'Order Succeeded');
    }
  }, [orderStatus]);

  useEffect(() => {
    if (!chartRef.current || !seriesRef.current) return;

    const handleCrosshairMove = (param) => {
      updateTooltip(param);
      let decimalPlaces;
      if (!selectedSymbolBid || isNaN(selectedSymbolBid)) {
        decimalPlaces = 2
      }
      else {
        const decimalPart = selectedSymbolBid.toString().split('.')[1];
        decimalPlaces = decimalPart ? decimalPart.length : 2; // fallback to 2
      }

      if (!param || !param.time || !param.seriesData || !param.seriesData.get(seriesRef.current)) {
        if (lastCandleRef.current) {
          setOhlcData({
            open: Number(lastCandleRef.current.open).toFixed(decimalPlaces) || Number(lastCandleRef.current.close).toFixed(decimalPlaces),
            high: Number(lastCandleRef.current.high).toFixed(decimalPlaces) || Number(lastCandleRef.current.close).toFixed(decimalPlaces),
            low: Number(lastCandleRef.current.low).toFixed(decimalPlaces) || Number(lastCandleRef.current.close).toFixed(decimalPlaces),
            close: Number(lastCandleRef.current.close),
            time: lastCandleRef.current.time,
            color: lastCandleRef.current.close >= lastCandleRef.current.open ? "#2DA479" : "#DC3A48"
          });
        }
        return;
      }

      const candle = param.seriesData.get(seriesRef.current);

      let ohlcUpdate;
      if (selectedSeriesType === "Candlestick" || selectedSeriesType === "Bar") {
        ohlcUpdate = {
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
        };
      } else {
        ohlcUpdate = {
          open: candle.value,
          high: candle.value,
          low: candle.value,
          close: candle.value,
        };
      }

      setOhlcData({
        ...ohlcUpdate,
        time: param.time,
        color: candle.close >= candle.open ? "#2DA479" : "#DC3A48",
      });
    };

    chartRef.current.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      chartRef.current.unsubscribeCrosshairMove(handleCrosshairMove);
    };
  }, [seriesRef.current, selectedSeriesType]);

  useEffect(() => {
    if (!chartRef.current) return;

    const handleTimeRangeChange = () => {
      if (!seriesRef.current || mappedData.length === 0 || isLoading) return;

      const invertedTF = selectedTimeframe.slice(-1).toUpperCase() + selectedTimeframe.slice(0, -1);

      const timeRange = chartRef.current.timeScale().getVisibleRange();
      if (!timeRange) return;

      visibleRangeRef.current = timeRange;

      const firstCandleTime = mappedData[0].time;
      const currentTime = Date.now() / 1000;

      if (
        timeRange.from <= firstCandleTime &&
        currentTime - lastFetchRef.current > 2 &&
        !isLoading
      ) {
        const nextDay = bars + 100;
        // console.log('Fetching data for day:', nextDay);

        setIsLoading(true);
        lastFetchRef.current = currentTime;
        // console.log('selectedTimeframe', selectedTimeframe);

        setbars(nextDay);
        dispatch(fetchMarketDataHistory({ symbol: selectedSymbol, timeframe: invertedTF, bars: nextDay }))
          .then(() => {
            setIsLoading(false);
          })
          .catch(() => {
            setIsLoading(false);
          });
      }
    };

    const timeScale = chartRef.current.timeScale();
    timeScale.subscribeVisibleTimeRangeChange(handleTimeRangeChange);

    return () => {
      timeScale.unsubscribeVisibleTimeRangeChange(handleTimeRangeChange);
    };
  }, [mappedData, bars, selectedSymbol, dispatch, isLoading, selectedTimeframe]);

  useEffect(() => {
    if (!selectedSymbol) {
      dispatch(setClickedSymbolData("BTCUSD"));
      return;
    }
    const fullName = getSymbolFullName(selectedSymbol);

    document.getElementById("chart-title").innerText = fullName;

    setIsLoading(true);
    setbars(100);
    lastFetchRef.current = Date.now() / 1000;

    dispatch(fetchMarketDataHistory({ symbol: selectedSymbol, timeframe: 'M1', bars: 100 }))
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [selectedSymbol, dispatch]);

  useEffect(() => {
    // if (status !== "succeeded") return;
    // if (!data || data.length === 0 || !seriesRef.current) return;
    if (!status || status !== "succeeded" || !data || data.length === 0 || !seriesRef.current) return;

    seriesRef.current.setData([]);

    if (status === "succeeded") {
      const transformedData = transformData(data, selectedSeriesType);
      seriesRef.current.setData(transformedData);

      // --- Remove all existing series before adding again ---
      if (emaSeriesRef.current) { chartRef.current.removeSeries(emaSeriesRef.current); emaSeriesRef.current = null; }
      if (smaSeriesRef.current) { chartRef.current.removeSeries(smaSeriesRef.current); smaSeriesRef.current = null; }
      if (wmaSeriesRef.current) { chartRef.current.removeSeries(wmaSeriesRef.current); wmaSeriesRef.current = null; }

      if (bbUpperSeriesRef.current) { chartRef.current.removeSeries(bbUpperSeriesRef.current); bbUpperSeriesRef.current = null; }
      if (bbLowerSeriesRef.current) { chartRef.current.removeSeries(bbLowerSeriesRef.current); bbLowerSeriesRef.current = null; }


      // --- Now Add Active Indicators ---
      activeIndicators.forEach(indicator => {
        if (indicator === 'EMA') {
          emaSeriesRef.current = chartRef.current.addLineSeries({ color: emaLineColor, lineWidth: 2 });
          const emaData = calculateEMA(mappedData, emaPeriod);
          emaSeriesRef.current.setData(emaData);
        }
        if (indicator === 'SMA') {
          smaSeriesRef.current = chartRef.current.addLineSeries({ color: smaLineColor, lineWidth: 2 });
          const smaData = calculateSMA(mappedData, smaPeriod);
          smaSeriesRef.current.setData(smaData);
        }
        if (indicator === 'WMA') {
          wmaSeriesRef.current = chartRef.current.addLineSeries({ color: wmaLineColor, lineWidth: 2 });
          const wmaData = calculateWMA(mappedData, wmaPeriod);
          wmaSeriesRef.current.setData(wmaData);
        }
        if (indicator === 'BollingerBands') {
          const { upper, lower } = calculateBB(mappedData, bbPeriod);
          bbUpperSeriesRef.current = chartRef.current.addLineSeries({ color: '#AAAAFF', lineWidth: 1 });
          bbLowerSeriesRef.current = chartRef.current.addLineSeries({ color: '#AAAAFF', lineWidth: 1 });
          bbUpperSeriesRef.current.setData(upper);
          bbLowerSeriesRef.current.setData(lower);
        }

      });

      if (visibleRangeRef.current && bars > 1) {
        chartRef.current.timeScale().setVisibleRange(visibleRangeRef.current);
      } else {
        const dataLength = transformedData.length;
        if (dataLength > 0) {
          const lastIndex = dataLength - 1;
          const startIndex = Math.max(0, lastIndex - 100);

          if (transformedData[startIndex] && transformedData[lastIndex]) {
            chartRef.current.timeScale().setVisibleRange({
              from: transformedData[startIndex].time,
              to: transformedData[lastIndex].time,
            });
          }

          // chartRef.current.timeScale().setVisibleRange({
          //   from: transformedData[startIndex].time,
          //   to: transformedData[lastIndex].time
          // });
        }
      }
    }
  }, [data, status, selectedSeriesType, bars, activeIndicators]);

  useEffect(() => {
    if (!status || status !== "succeeded" || !data || data.length === 0 || !seriesRef.current) return;

    // console.log("✅ Rendering OHLC History Data:", data);

    seriesRef.current.setData([]); // Clear previous data

    // const transformedData = data.map(item => ({
    const rawData = data.map(item => ({
      time: Math.floor(new Date(item.time).getTime() / 1000),
      open: item.open,
      high: item.high,
      low: item.low,
      close: item.close,
    }));

    const transformedData = transformData(rawData, selectedSeriesType);
    seriesRef.current.setData(transformedData);

    // --- Remove all existing series before adding again ---
    if (emaSeriesRef.current) { chartRef.current.removeSeries(emaSeriesRef.current); emaSeriesRef.current = null; }
    if (smaSeriesRef.current) { chartRef.current.removeSeries(smaSeriesRef.current); smaSeriesRef.current = null; }
    if (wmaSeriesRef.current) { chartRef.current.removeSeries(wmaSeriesRef.current); wmaSeriesRef.current = null; }

    if (bbUpperSeriesRef.current) { chartRef.current.removeSeries(bbUpperSeriesRef.current); bbUpperSeriesRef.current = null; }
    if (bbLowerSeriesRef.current) { chartRef.current.removeSeries(bbLowerSeriesRef.current); bbLowerSeriesRef.current = null; }

    // --- Now Add Active Indicators ---
    activeIndicators.forEach(indicator => {
      if (indicator === 'EMA') {
        emaSeriesRef.current = chartRef.current.addLineSeries({ color: emaLineColor, lineWidth: 2 });
        const emaData = calculateEMA(mappedData, emaPeriod);
        emaSeriesRef.current.setData(emaData);
      }
      if (indicator === 'SMA') {
        smaSeriesRef.current = chartRef.current.addLineSeries({ color: smaLineColor, lineWidth: 2 });
        const smaData = calculateSMA(mappedData, smaPeriod);
        smaSeriesRef.current.setData(smaData);
      }
      if (indicator === 'WMA') {
        wmaSeriesRef.current = chartRef.current.addLineSeries({ color: wmaLineColor, lineWidth: 2 });
        const wmaData = calculateWMA(mappedData, wmaPeriod);
        wmaSeriesRef.current.setData(wmaData);
      }
      if (indicator === 'BollingerBands') {
        const { upper, lower } = calculateBB(mappedData, bbPeriod);
        bbUpperSeriesRef.current = chartRef.current.addLineSeries({ color: '#AAAAFF', lineWidth: 1 });
        bbLowerSeriesRef.current = chartRef.current.addLineSeries({ color: '#AAAAFF', lineWidth: 1 });
        bbUpperSeriesRef.current.setData(upper);
        bbLowerSeriesRef.current.setData(lower);
      }

    });

  }, [data, selectedSeriesType, status, activeIndicators]);

  useEffect(() => {
    if (!chartContainerRef.current) return;
    chartRef.current = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight || 400,
      layout: {
        background: {
          type: "solid",
          color: layoutMode === "dark" ? "#121416" : "#ffffff",
        },
        textColor: layoutMode === "dark" ? "#ffffff" : "#000000",
      },
      grid: {
        vertLines: { color: layoutMode === "dark" ? "#121416" : "#eee" },
        horzLines: { color: layoutMode === "dark" ? "#121416" : "#eee" }
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 6,
        barSpacing: 12,
        minBarSpacing: 2,
        fixLeftEdge: false,
      },
      priceScale: {
        scaleMargins: {
          top: 0.1,
          bottom: 0.2,
        },
        minMove: 0.0001,
        autoScale: true,
      },
      crosshair: {
        mode: 1,
        vertLine: {
          width: 1,
          color: layoutMode === "dark" ? "#4a4a4a" : "#c1c1c1",
          style: 2,
        },
        horzLine: {
          width: 1,
          color: layoutMode === "dark" ? "#4a4a4a" : "#c1c1c1",
          style: 2,
        },
      },
    });
    seriesRef.current = createSeries(chartRef.current, selectedSeriesType);
    const resizeObserver = new ResizeObserver(() => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    });
    resizeObserver.observe(chartContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      chartRef.current.remove();
      chartRef.current = null;
    };
  }, [layoutMode]);

  useEffect(() => {
    if (!selectedSymbol || !symbols[selectedSymbol] || !chartRef.current) return;
    if (seriesRef.current) {
      try {
        chartRef.current.removeSeries(seriesRef.current);
      } catch (error) {
        console.error("Error removing series:", error);
      }
      seriesRef.current = null;
    }
    seriesRef.current = createSeries(chartRef.current, selectedSeriesType);
    lastCandleRef.current = null;

    if (selectedSeriesType) {
      if (selectedSymbol && mappedData.length > 0) {
        const transformedData = transformData(mappedData, selectedSeriesType);
        seriesRef.current.setData(transformedData);

        // --- Remove all existing series before adding again ---
        if (emaSeriesRef.current) { chartRef.current.removeSeries(emaSeriesRef.current); emaSeriesRef.current = null; }
        if (smaSeriesRef.current) { chartRef.current.removeSeries(smaSeriesRef.current); smaSeriesRef.current = null; }
        if (wmaSeriesRef.current) { chartRef.current.removeSeries(wmaSeriesRef.current); wmaSeriesRef.current = null; }

        if (bbUpperSeriesRef.current) { chartRef.current.removeSeries(bbUpperSeriesRef.current); bbUpperSeriesRef.current = null; }
        if (bbLowerSeriesRef.current) { chartRef.current.removeSeries(bbLowerSeriesRef.current); bbLowerSeriesRef.current = null; }


        // --- Now Add Active Indicators ---
        activeIndicators.forEach(indicator => {
          if (indicator === 'EMA') {
            emaSeriesRef.current = chartRef.current.addLineSeries({ color: emaLineColor, lineWidth: 2 });
            const emaData = calculateEMA(mappedData, emaPeriod);
            emaSeriesRef.current.setData(emaData);
          }
          if (indicator === 'SMA') {
            smaSeriesRef.current = chartRef.current.addLineSeries({ color: smaLineColor, lineWidth: 2 });
            const smaData = calculateSMA(mappedData, smaPeriod);
            smaSeriesRef.current.setData(smaData);
          }
          if (indicator === 'WMA') {
            wmaSeriesRef.current = chartRef.current.addLineSeries({ color: wmaLineColor, lineWidth: 2 });
            const wmaData = calculateWMA(mappedData, wmaPeriod);
            wmaSeriesRef.current.setData(wmaData);
          }
          if (indicator === 'BollingerBands') {
            const { upper, lower } = calculateBB(mappedData, bbPeriod);
            bbUpperSeriesRef.current = chartRef.current.addLineSeries({ color: '#AAAAFF', lineWidth: 1 });
            bbLowerSeriesRef.current = chartRef.current.addLineSeries({ color: '#AAAAFF', lineWidth: 1 });
            bbUpperSeriesRef.current.setData(upper);
            bbLowerSeriesRef.current.setData(lower);
          }

        });


      }
    }
  }, [selectedSymbol, selectedTimeframe, selectedSeriesType, mappedData]);

  useEffect(() => {
    if (!selectedSymbol || !symbols[selectedSymbol] || !seriesRef.current) return;


    // const { bid } = symbols[selectedSymbol];
    const bid = parseFloat(symbols[selectedSymbol]?.bid);
    if (isNaN(bid)) return;


    // console.log('bid', bid);

    // const currentTime = Math.floor(Date.now() / 1000);
    const currentTime = Math.floor(Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
      new Date().getUTCHours(),
      new Date().getUTCMinutes(),
      new Date().getUTCSeconds()
    ) / 1000);

    const currentTimeGMT2 = currentTime + 21600;
    const timeframeSeconds = timeframes[selectedTimeframe];
    const candleTime = Math.floor(currentTimeGMT2 / timeframeSeconds) * timeframeSeconds;

    let newCandle = { ...lastCandleRef.current };

    if (!lastCandleRef.current || lastCandleRef.current.time !== candleTime) {
      newCandle = {
        time: candleTime,
        open: bid,
        high: bid,
        low: bid,
        close: bid,
      };
    } else {
      newCandle.high = Math.max(lastCandleRef.current.high, bid);
      newCandle.low = Math.min(lastCandleRef.current.low, bid);
      newCandle.close = bid;
    }

    if (mappedData.length > 0 && newCandle.time <= mappedData[mappedData.length - 1].time) {
      return; // Prevent duplicate timestamps
    }

    lastCandleRef.current = newCandle;

    setOhlcData((prev) => ({
      open: newCandle.open,
      high: newCandle.high,
      low: newCandle.low,
      close: newCandle.close,
      color: newCandle.close >= newCandle.open ? "#2DA479" : "#DC3A48",
    }));

    if (seriesRef.current && status === 'succeeded') {
      const transformedCandle = transformData([newCandle], selectedSeriesType)[0];
      seriesRef.current.update(transformedCandle);

      // Update active indicators with the newCandle
      if (activeIndicators.includes('EMA') && emaSeriesRef.current) {
        const updatedEma = calculateEMA([...mappedData, newCandle], emaPeriod).pop(); // only latest point
        emaSeriesRef.current.update(updatedEma);
      }

      if (activeIndicators.includes('SMA') && smaSeriesRef.current) {
        const updatedSma = calculateSMA([...mappedData, newCandle], smaPeriod).pop();
        smaSeriesRef.current.update(updatedSma);
      }

      if (activeIndicators.includes('WMA') && wmaSeriesRef.current) {
        const updatedWma = calculateWMA([...mappedData, newCandle], wmaPeriod).pop();
        wmaSeriesRef.current.update(updatedWma);
      }

      if (activeIndicators.includes('BollingerBands') && bbUpperSeriesRef.current && bbLowerSeriesRef.current) {
        const { upper, lower } = calculateBB([...mappedData, newCandle], bbPeriod);
        bbUpperSeriesRef.current.update(upper[upper.length - 1]);
        bbLowerSeriesRef.current.update(lower[lower.length - 1]);
      }
    }
  }, [symbols, selectedSymbol, selectedTimeframe, selectedSeriesType, status]);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.timeScale().applyOptions({
        rightOffset: 6,  // Ensure rightOffset is applied on data update
        barSpacing: 12,
        minBarSpacing: 2,
        fixLeftEdge: false,
      });
    }
  }, [mappedData, selectedSymbol, selectedTimeframe, selectedSeriesType]);

  return (
    <div className="chart-wrapper">
      <div className="chart-topbar w-100 d-flex align-items-center gap-2 p-2" style={{ background: layoutMode === "dark" ? "#121416" : "#D7D7D7" }}>
        <Select
          options={options}
          value={options.find(option => option.value === (selectedSymbol || "BTCUSD.ex1"))}
          onChange={handleSelectChange}
          placeholder={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="ri-search-line" style={{ color: '#6c757d' }}></i>
              <span>Select a symbol</span>
            </div>
          }
          className="symbol-select p-0"
          isSearchable={true}
        />

        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="btn btn-outline-light">
          <DropdownToggle tag="i" className="ri-hourglass-fill" style={{ cursor: 'pointer' }} />
          <DropdownMenu end>
            {Object.keys(timeframes).map((tf) => (
              <DropdownItem
                key={tf}
                onClick={() => {
                  const invertedTF = tf.slice(-1).toUpperCase() + tf.slice(0, -1);
                  setSelectedTimeframe(tf)
                  setbars(100)

                  if (seriesRef.current) {
                    seriesRef.current.setData([]);
                  }

                  dispatch(fetchMarketDataHistory({ symbol: selectedSymbol, timeframe: invertedTF, bars: bars }))
                }}
                className={selectedTimeframe === tf ? "active" : ""}
              >
                {tf}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "10px" }}>
          <Dropdown isOpen={indicatorDropdownOpen} toggle={() => setIndicatorDropdownOpen(!indicatorDropdownOpen)} className="btn btn-outline-light">
            <DropdownToggle caret style={{ padding: "1px", color: "#fff", backgroundColor: "transparent", borderColor: "transparent" }}>
              Indicators
            </DropdownToggle>
            <DropdownMenu style={{ backgroundColor: "#010e1c", borderColor: "#6c757d" }}>
              <DropdownItem onClick={() => toggleModal('EMA')} style={{ color: '#fff' }}>EMA</DropdownItem>
              <DropdownItem onClick={() => toggleModal('SMA')} style={{ color: '#fff' }}>SMA</DropdownItem>
              <DropdownItem onClick={() => toggleModal('WMA')} style={{ color: '#fff' }}>WMA</DropdownItem>
              <DropdownItem onClick={() => toggleIndicator('BollingerBands')} style={{ color: '#fff' }}>Bollinger Bands</DropdownItem>
              {/* <DropdownItem onClick={() => toggleIndicator('RSI')} style={{ color: '#fff' }}>RSI</DropdownItem>
              <DropdownItem onClick={() => toggleIndicator('MACD')} style={{ color: '#fff' }}>MACD</DropdownItem>
              <DropdownItem onClick={() => toggleIndicator('Volume')} style={{ color: '#fff' }}>Volume Bars</DropdownItem>
              <DropdownItem onClick={() => toggleIndicator('ATR')} style={{ color: '#fff' }}>ATR</DropdownItem>
              <DropdownItem onClick={() => toggleIndicator('Stochastic')} style={{ color: '#fff' }}>Stochastic Oscillator</DropdownItem> */}
              <DropdownItem divider />
              <DropdownItem onClick={clearAllIndicators} style={{ color: 'red' }}>Clear All Indicators</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
        <Modal isOpen={modal} toggle={toggleModal} className='invoice-modal' >
          <ModalBody>
            <IndicatorsSettings onInputChange={handleSettings} indicatorName={selectedIndicator} />
          </ModalBody>
          <ModalFooter>
            {/* <Button color="default" onClick={() => toggleModal()}>{t('Close')}</Button> */}
            {/* <Button color="secondary" onClick={() => {toggleIndicator("EMA")}}>{t('Remove')}</Button> */}
            <Button color="success" onClick={() => { toggleIndicator(selectedIndicator); toggleModal() }}>Apply</Button>
          </ModalFooter>
        </Modal>

        <Dropdown title="Take Screenshot" isOpen={seriesDropdownOpen} toggle={toggleSeriesDropdown} className="btn btn-outline-light">
          <DropdownToggle tag="i" className="ri-line-chart-line" style={{ cursor: 'pointer' }} />
          <DropdownMenu end>
            {seriesTypes.map((type) => (
              <DropdownItem
                key={type}
                onClick={() => handleSeriesTypeChange(type)}
                className={selectedSeriesType === type ? "active" : ""}
              >
                {type}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>

        <button
          onClick={handleScreenshot}
          className="btn btn-outline-light p-2 px-3"
          title="Take Screenshot"
        >
          <i className="ri-camera-fill"></i>
        </button>
        <button
          onClick={() => {
            chartRef.current.timeScale().scrollToRealTime();
          }}
          className="btn btn-outline-light p-2 px-3"
          title="Go to realtime"
        >
          <i className="ri-rfid-line"></i>
        </button>
      </div>

      <div className="d-flex position-absolute" style={{ top: '65px', left: '10px', zIndex: 9, }}>
        <p id="chart-title" className="m-0 me-4" style={{ color: layoutMode === "dark" ? "#ffffff" : "#000000" }}></p>
        <div className="ohlc-info" style={{ color: ohlcData.color, }}>
          <strong className="text-muted fw-bold">O:</strong> {ohlcData.open} |
          <strong className="text-muted fw-bold"> H:</strong> {ohlcData.high} |
          <strong className="text-muted fw-bold"> L:</strong> {ohlcData.low} |
          <strong className="text-muted fw-bold"> C:</strong> {ohlcData.close}
        </div>
      </div>

      <div ref={chartContainerRef} className="chart-container" style={{ position: 'relative' }}>
        {tooltipVisible && tooltipData && (
          <div
            ref={tooltipRef}
            style={{
              position: 'absolute',
              left: `${tooltipPosition.x + 15}px`,
              top: `${tooltipPosition.y}px`,
              backgroundColor: layoutMode === "dark" ? 'rgba(26, 29, 33, 0.9)' : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${layoutMode === "dark" ? '#4a4a4a' : '#c1c1c1'}`,
              padding: '8px',
              borderRadius: '4px',
              pointerEvents: 'none', zIndex: 10,
              color: layoutMode === "dark" ? '#ffffff' : '#000000',
              fontSize: '12px',
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ marginBottom: '4px' }}>{tooltipData.time}</div>
            {selectedSeriesType === 'Candlestick' || selectedSeriesType === 'Bar' ? (
              <>
                <div style={{ color: '#2DA479' }}>Open: {tooltipData.data.open}</div>
                <div style={{ color: '#2DA479' }}>High: {tooltipData.data.high}</div>
                <div style={{ color: '#DC3A48' }}>Low: {tooltipData.data.low}</div>
                <div style={{ color: tooltipData.data.close >= tooltipData.data.open ? '#2DA479' : '#DC3A48' }}>
                  Close: {tooltipData.data.close}
                </div>
              </>
            ) : (
              <div style={{ color: '#2962FF' }}>Value: {tooltipData.data.value}</div>
            )}
          </div>
        )}

        <div id="zoom-controls" className="d-flex justify-content-center align-items-center w-100 gap-2" style={{ position: 'absolute', bottom: 45, zIndex: 999 }}>
          <button onClick={zoomIn} className="bg-transparent border-0 fs-5"><i class="ri-zoom-in-line"></i></button>
          <button onClick={zoomOut} className="bg-transparent border-0 fs-5"><i class="ri-zoom-out-line"></i></button>
          <button onClick={resetZoom} className="bg-transparent border-0 fs-5"><i class="ri-refresh-line"></i></button>
        </div>
      </div>
    </div>
  );
};

export default TradingViewChart;